import React from 'react';
import { Stack, Button, Group, Title, Center } from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import DeleteConfirmationModal from '../common/DeleteConfirmationModal';
import { useClient } from '../../context/ClientContext';
import CampaignSetupStep from './CampaignSetupStep';
import ClientSelectionStep from './ClientSelectionStep';
import MessageCreationStep from './MessageCreationStep';
import ComplianceCheckStep from './ComplianceCheckStep';
import FinalReviewStep from './FinalReviewStep';
import { useCampaign } from '../../context/CampaignContext';

function CampaignWizard() {
  const navigate = useNavigate();
  const {
    step, setStep,
    name, description,
    setErrors, selectedClientIds, 
    message, showDiscardModal, 
    setShowDiscardModal,
    resetCampaign,
    createCampaign,
    campaignCreationError
  } = useCampaign();

  const { clients, loading: clientsLoading } = useClient();
  const creationDate = new Date().toLocaleDateString();

  const validate = () => {
    const errs = {};
    if (!name.trim()) errs.name = 'Campaign name is required.';
    if (description.trim().length < 10) errs.description = 'Description must be at least 10 characters.';
    if (description.trim().length > 256) errs.description = 'Description must be at most 256 characters.';
    return errs;
  };

  const handleNext = () => {
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length === 0) {
      setStep(2); // placeholder for next step
    }
  };

  const handleCancel = () => {
    if (name || description || selectedClientIds.length > 0) {
      setShowDiscardModal(true);
    } else {
      setStep(1); // Always reset to step 1 before navigating away
      navigate('/admin/marketing');
    }
  };

  const handleSubmit = async () => {
    const response = await createCampaign();
    
    if (response) {
      const isPlural = response.message_count > 1 ? 's' : '';
      notifications.show({
        title: 'Success!',
        message: `Created campaign and scheduled ${response.message_count} message${isPlural}`,
        color: 'green',
      });
      resetCampaign();
      navigate('/admin/marketing');
    } else {
      notifications.show({
        title: 'Error',
        message: campaignCreationError || 'Failed to create campaign',
        color: 'red',
      });
    }
  };

  return (
    <Stack p="xl" maw={800} mx="auto" spacing="xl">
      <Group position="apart" mb="md" align="center" style={{ minHeight: 56 }}>
        <Button variant="subtle" color="gray" onClick={handleCancel} style={{ minWidth: 160, textAlign: 'left' }}>&larr; Back to Marketing</Button>
        <Center style={{ flex: 1 }}>
          <Title order={2} style={{ textAlign: 'center' }}>New Campaign</Title>
        </Center>
        <div style={{ minWidth: 160 }} />
      </Group>
      {step === 1 && (
        <CampaignSetupStep
          onNext={handleNext}
        />
      )}
      {step === 2 && (
        <ClientSelectionStep
          clients={clients}
          loading={clientsLoading}
          onBack={() => setStep(1)}
          onNext={() => selectedClientIds.length > 0 && setStep(3)}
        />
      )}
      {step === 3 && (
        <MessageCreationStep
          onBack={() => setStep(2)}
          onNext={() => setStep(4)}
        />
      )}
      {step === 4 && (
        <ComplianceCheckStep />
      )}
      {step === 5 && (
        <FinalReviewStep
          date={creationDate}
          onBack={() => setStep(3)}
          onSubmit={handleSubmit}
        />
      )}
      <DeleteConfirmationModal
        isOpen={showDiscardModal}
        onClose={() => setShowDiscardModal(false)}
        onConfirm={() => { setStep(1); navigate('/admin/marketing'); }}
        title="Discard Campaign?"
        message="Are you sure you want to discard this campaign? All progress will be lost."
      />
    </Stack>
  );
}

export default CampaignWizard; 